import { makeStyles, Theme } from '@material-ui/core/styles'

export const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        height: 'calc(100vh - 56px - 48px)',
        overflow: 'hidden',
    },
    headerSection: {
        marginBottom: theme.spacing(1),
        flexShrink: 0,
    },
    statsCard: {
        borderRadius: 12,
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    statsIconBox: {
        padding: theme.spacing(1),
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    filterRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexShrink: 0,
    },
    tableCard: {
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column' as const,
    },
    tableScrollWrapper: {
        flex: 1,
        minHeight: 0,
        position: 'relative' as const,
        display: 'flex',
        flexDirection: 'column' as const,
    },
    tableHeader: {
        backgroundColor: '#f8fafc',
    },
    tableHeaderCell: {
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        color: '#6b7280',
        backgroundColor: '#f8fafc',
    },
    tableRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(248, 250, 252, 0.5)',
        },
    },
    amountPositive: {
        color: '#10b981',
        fontWeight: 600,
    },
    amountNegative: {
        color: '#ef4444',
        fontWeight: 600,
    },

    // Detail Dialog
    dialogPaper: {
        borderRadius: 12,
        maxWidth: 900,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
    },
    dialogHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    dialogHeaderCenter: {
        textAlign: 'center',
        flex: 1,
    },
    dialogContent: {
        padding: theme.spacing(3),
        overflowY: 'auto',
        maxHeight: 'calc(90vh - 80px)',
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    infoCard: {
        backgroundColor: theme.palette.grey[50],
        borderRadius: 8,
        padding: theme.spacing(2),
    },
    dateCard: {
        backgroundColor: theme.palette.grey[50],
        borderRadius: 8,
        padding: theme.spacing(1.5),
        textAlign: 'center' as const,
    },
    conditionCard: {
        backgroundColor: theme.palette.grey[50],
        borderRadius: 8,
        padding: theme.spacing(2),
    },
    videoSection: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        overflow: 'hidden',
    },
    videoHeader: {
        backgroundColor: theme.palette.grey[50],
        padding: theme.spacing(1.5, 2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    videoPlaceholder: {
        aspectRatio: '16 / 9',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    damageSection: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        overflow: 'hidden',
    },
    damageHeader: {
        backgroundColor: 'rgba(239, 68, 68, 0.08)',
        padding: theme.spacing(1.5, 2),
    },
    damageRow: {
        padding: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    damagePhoto: {
        width: 80,
        height: 80,
        objectFit: 'cover' as const,
        borderRadius: 8,
        border: `1px solid ${theme.palette.divider}`,
    },
    damageTotal: {
        backgroundColor: theme.palette.grey[50],
        padding: theme.spacing(1.5, 2),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    chargeCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(2),
    },
    dispositionSection: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        overflow: 'hidden',
    },
    dispositionHeader: {
        backgroundColor: theme.palette.grey[50],
        padding: theme.spacing(1.5, 2),
    },
    dispositionBody: {
        padding: theme.spacing(2),
    },
    dispositionRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing(1, 0),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    dispositionSubtotal: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing(1, 2),
        margin: theme.spacing(0, -2),
        backgroundColor: theme.palette.grey[50],
        fontWeight: 600,
    },
    finalAmountBox: {
        padding: theme.spacing(2),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    legalNotice: {
        backgroundColor: theme.palette.grey[50],
        borderRadius: 8,
        padding: theme.spacing(2),
    },
    actionBar: {
        display: 'flex',
        gap: theme.spacing(1.5),
        paddingTop: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },

    // Statement / Email Config Dialog
    configDialogPaper: {
        borderRadius: 12,
    },
    configDialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 3),
    },
    configDialogContent: {
        padding: theme.spacing(3),
    },
    configDialogActions: {
        padding: theme.spacing(2, 3),
    },
    configSectionLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(0.5),
    },
    logoPreviewBox: {
        width: 120,
        height: 80,
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.grey[50],
        overflow: 'hidden',
    },

    // Financial Connection Awareness
    financialBanner: {
        backgroundColor: 'rgba(245, 158, 11, 0.08)',
        borderLeft: '4px solid #f59e0b',
        borderRadius: 8,
        padding: theme.spacing(1.5, 2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexShrink: 0,
    },
    financialBannerText: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    connectButton: {
        borderColor: '#f59e0b',
        color: '#92400e',
        '&:hover': {
            borderColor: '#d97706',
            backgroundColor: 'rgba(245, 158, 11, 0.08)',
        },
    },
    lastSyncCaption: {
        color: '#6b7280',
        fontSize: '0.7rem',
        whiteSpace: 'nowrap' as const,
    },
    syncProgressRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 180,
    },
    syncProgressBar: {
        flex: 1,
        height: 6,
        borderRadius: 3,
    },
    syncProgressLabel: {
        color: '#3b82f6',
        fontSize: '0.7rem',
        fontWeight: 600,
        whiteSpace: 'nowrap' as const,
    },
    syncFailureCaption: {
        color: '#ef4444',
        fontSize: '0.7rem',
        whiteSpace: 'nowrap' as const,
    },
    financialNoticeCard: {
        backgroundColor: 'rgba(245, 158, 11, 0.08)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: 8,
        padding: theme.spacing(3),
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
    },

    // Config Dialog – Preview Thumbnails
    previewThumbnail: {
        width: 140,
        height: 80,
        objectFit: 'cover',
        borderRadius: 6,
        border: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        flexShrink: 0,
        marginLeft: theme.spacing(2),
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
        '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            transform: 'scale(1.03)',
        },
    },
    previewLightboxPaper: {
        borderRadius: 12,
        overflow: 'hidden',
        position: 'relative' as const,
        maxWidth: '90vw',
    },
    previewLightboxClose: {
        position: 'absolute' as const,
        top: 8,
        right: 8,
        backgroundColor: 'rgba(255,255,255,0.85)',
        zIndex: 1,
        '&:hover': {
            backgroundColor: '#fff',
        },
    },
}))
